/**
 * ErrorBoundary — cattura crash React che altrimenti causano schermata bianca.
 *
 * ★ 2026-07-21 (bug fix schermata bianca affiliati):
 * Prima, un errore nel rendering di una route producva un unmount silenzioso e
 * la pagina restava vuota. Ora, in caso di eccezione, mostriamo un fallback UI
 * con opzione di ripristino.
 */
import React from 'react';
import i18n from '../i18n';
import { logger } from '../lib/logger';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('[ErrorBoundary] React crash captured:', error, errorInfo);
    // Best-effort remote logging
    try {
      const payload = {
        message: error?.message || String(error),
        stack: error?.stack?.slice(0, 2000),
        componentStack: errorInfo?.componentStack?.slice(0, 2000),
        url: typeof window !== 'undefined' ? window.location.href : '',
      };
      // Fire-and-forget: non aspettiamo la risposta
      fetch('/api/_ui/log-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch (e) { /* noop */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen grid place-items-center bg-[var(--surface-1,#fbfaf6)] p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-red-200 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 grid place-items-center text-red-600 text-xl">⚠️</div>
              <div>
                <h1 className="font-serif text-lg text-red-900">{i18n.t('errorBoundary.title', 'Errore imprevisto')}</h1>
                <p className="text-xs text-muted-foreground">
                  {i18n.t('errorBoundary.subtitle', 'La pagina ha incontrato un problema di rendering.')}
                </p>
              </div>
            </div>

            <div className="rounded bg-red-50 border border-red-100 p-3 text-xs text-red-900 font-mono">
              {this.state.error?.message || i18n.t('errorBoundary.unknown', 'Errore sconosciuto')}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={this.handleReset}
                className="flex-1 py-2 px-3 rounded-md border border-border text-sm hover:bg-muted transition-colors"
                data-testid="error-boundary-retry"
              >
                {i18n.t('errorBoundary.retry', 'Riprova')}
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                className="flex-1 py-2 px-3 rounded-md bg-[hsl(var(--primary))] text-white text-sm hover:opacity-90 transition-opacity"
                data-testid="error-boundary-home"
              >
                {i18n.t('errorBoundary.goLogin', 'Torna al login')}
              </button>
            </div>

            <p className="text-[10px] text-muted-foreground text-center">
              {i18n.t('errorBoundary.logged', "L'errore è stato registrato. Se persiste, contatta il team tecnico.")}
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
